/**
 * The brokered inference work plane's console endpoints — the relay fleet, the
 * job queue and the operator's withdrawal (PRD-0254 D-11).
 *
 * Only the five routes a browser calls live here. The eleven-route family also
 * carries the machine surface a relay drives — register, heartbeat, claim,
 * lease renewal, result post — and those are reached with a rank-0 relay token
 * by the relay process itself, never by this client.
 *
 * Every path carries the trailing slash the router declares, for the same
 * reason the governance module does: FastAPI 307-redirects a missing one
 * before auth runs, and the client would then follow an unauthenticated
 * redirect into a 401.
 */

import type {
  CancelJobRequest,
  InferenceJob,
  InferenceJobFilters,
  InferenceJobListResponse,
  ModelRelay,
  ModelRelayListResponse,
  RelayFilters,
} from '@/types/modelCapabilities'
import { apiClient } from './client'

const BASE = '/api/v1/model-relay'

export const modelRelay = {
  // Not project-filterable, and the route says so: a relay is an
  // organisation-wide executor with no project dimension to narrow to.
  listRelays: async (
    filters: RelayFilters = {}
  ): Promise<ModelRelayListResponse> => {
    const response = await apiClient.get<ModelRelayListResponse>(
      `${BASE}/relays/`,
      { params: filters }
    )
    return response.data
  },

  getRelay: async (relayId: string): Promise<ModelRelay> => {
    const response = await apiClient.get<ModelRelay>(
      `${BASE}/relays/${relayId}/`
    )
    return response.data
  },

  // The job's project is not in the path: the route confines the page to the
  // caller's own projects plus the organisation-wide rows, so a narrowing the
  // client applied on top would only ever hide rows it was already served.
  listJobs: async (
    filters: InferenceJobFilters = {}
  ): Promise<InferenceJobListResponse> => {
    const response = await apiClient.get<InferenceJobListResponse>(
      `${BASE}/jobs/`,
      { params: filters }
    )
    return response.data
  },

  getJob: async (jobId: string): Promise<InferenceJob> => {
    const response = await apiClient.get<InferenceJob>(`${BASE}/jobs/${jobId}/`)
    return response.data
  },

  // Answers with the settled row rather than an acknowledgement: the cancel
  // settles the job inline so whoever was waiting on it is released at once.
  // An already-terminal job is a 409 the shared error table renders inline.
  cancelJob: async (
    jobId: string,
    body: CancelJobRequest
  ): Promise<InferenceJob> => {
    const response = await apiClient.post<InferenceJob>(
      `${BASE}/jobs/${jobId}/cancel/`,
      body
    )
    return response.data
  },
}
